import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Import, ImportCompletedPayload, TerminalImportState } from '@klareospace/pharos-lib-types';
import { IMPORT_STATE } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { createImportsApi, importKeys } from '../api';
import type { ImportTypeConfig } from '../config';
import { showImportCompletion, showImportPending } from './use-import-notification-store';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Projects the upload response onto the same shape the realtime completion event carries. */
function toCompletedPayload(importDoc: Import, state: TerminalImportState): ImportCompletedPayload {
  return {
    fileName: importDoc.fileName,
    importId: importDoc.id,
    kind: importDoc.kind,
    linesCount: importDoc.linesCount,
    state,
    userId: importDoc.user.id,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Uploads an import file to the endpoint declared by `config`. On success, the import history
 * list cache is invalidated (so a user returning to the history page inside the list's stale
 * window sees the new row immediately) before the mutation navigates back to the orders list.
 * Then either:
 * - shows the "import in progress" pending card (top-right, sliding in from the side — same slot
 *   and animation as the completion card) when the import is still `pending`. It auto-dismisses
 *   after 5 seconds, or earlier when the completion event replaces it in the same slot, and can
 *   also be closed via its own close button, or
 * - shows the completion popup immediately for a born-terminal import (zero valid lines) — no
 *   realtime event will ever be published for it.
 * On error, no notification is shown: the caller reads `error` to render it inline.
 */
export function useImportFile(config: ImportTypeConfig): UseMutationResult<Import, Error, File> {
  const client = useApiClient('deliveries');
  const api = createImportsApi(client);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation<Import, Error, File>({
    mutationFn: (file: File): Promise<Import> => api.importFile(config.uploadEndpoint, file),
    onSuccess: (data): void => {
      void queryClient.invalidateQueries({ queryKey: importKeys.lists() });
      void navigate({ to: '/orders' });

      if (data.state === IMPORT_STATE.PENDING) {
        showImportPending(data.id);
        return;
      }
      showImportCompletion(toCompletedPayload(data, data.state));
    },
  });
}
